import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MASTER_COPY_GROUPS,
  MASTER_COPY_GROUP_DEPENDS_ON,
  MASTER_COPY_GROUP_LABELS,
  type CopyMastersResult,
  type MasterCopyGroup,
} from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface FacilityOption {
  id: string;
  name: string;
  slug: string;
  facilityType: string;
}

/**
 * Copy another facility's master data in (LIMS parity).
 *
 * Standing up a new hospital meant re-keying the charge master, the catalogue
 * and the payers by hand. Nothing here overwrites: a code the target already
 * has is left exactly as its own staff set it, and the result says how many
 * rows went in, how many were already there, and how many were dropped because
 * their parent has no match here.
 */
export function CopyMastersDialog({
  tenant,
  open,
  onClose,
}: {
  tenant: { id: string; name: string; facilityType: string };
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [sourceId, setSourceId] = useState('');
  const [groups, setGroups] = useState<MasterCopyGroup[]>([]);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CopyMastersResult | null>(null);

  const facilities = useQuery({
    queryKey: ['tenants', 'copy-source', tenant.facilityType],
    queryFn: () =>
      get<{ data: FacilityOption[] }>(`/platform/tenants?facilityType=${tenant.facilityType}&limit=100`),
    enabled: open,
  });

  // Only a facility of the same kind: a medical store's formulary is no use to
  // a laboratory, and offering it invites a copy nobody wants.
  const sources = (facilities.data?.data ?? []).filter((f) => f.id !== tenant.id);

  const toggle = (group: MasterCopyGroup, on: boolean) => {
    setGroups((prev) => {
      if (!on) return prev.filter((g) => g !== group);
      // Tick what this group needs as well, or its rows arrive with nothing to
      // point at and are silently dropped.
      const withDeps = [group, ...(MASTER_COPY_GROUP_DEPENDS_ON[group] ?? [])];
      return [...new Set([...prev, ...withDeps])];
    });
  };

  const copy = useMutation({
    mutationFn: () =>
      post<CopyMastersResult>(`/platform/tenants/${tenant.id}/copy-masters`, {
        totpCode,
        sourceTenantId: sourceId,
        groups,
      }),
    onSuccess: (data) => {
      const total = data.results.reduce((sum, r) => sum + r.copied, 0);
      toast.success(
        `${total} record${total === 1 ? '' : 's'} copied into ${data.targetName}`,
        total === 0 ? 'Everything was already there' : undefined,
      );
      setResult(data);
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const close = () => {
    setSourceId('');
    setGroups([]);
    setTotpCode('');
    setError(null);
    setResult(null);
    onClose();
  };

  if (result) {
    return (
      <Dialog
        open={open}
        onClose={close}
        wide
        title="Copied"
        description={`From ${result.sourceName} into ${result.targetName}`}
        footer={<Button onClick={close}>Done</Button>}
      >
        <DataTable
          rows={result.results}
          rowKey={(row) => row.group}
          columns={[
            { header: 'Group', cell: (row) => MASTER_COPY_GROUP_LABELS[row.group] },
            { header: 'Copied', align: 'right', cell: (row) => <span className="tabular">{row.copied}</span> },
            {
              header: 'Already there',
              align: 'right',
              cell: (row) => <span className="tabular text-muted-foreground">{row.skippedExisting}</span>,
            },
            {
              header: 'Dropped',
              align: 'right',
              cell: (row) =>
                row.skippedUnresolved > 0 ? (
                  <Badge variant="warning">{row.skippedUnresolved}</Badge>
                ) : (
                  <span className="tabular text-muted-foreground">0</span>
                ),
            },
          ]}
        />
        <p className="mt-3 text-xs text-muted-foreground">
          “Already there” rows were left exactly as this facility had them — a copy never overwrites a price its
          own staff agreed. “Dropped” rows pointed at a department, service or test that does not exist here;
          copy that group too and run this again.
        </p>
      </Dialog>
    );
  }

  const missing: string[] = [];
  if (!sourceId) missing.push('a facility to copy from');
  if (groups.length === 0) missing.push('at least one group');
  if (totpCode.trim().length < 6) missing.push('your authenticator code');

  return (
    <Dialog
      open={open}
      onClose={close}
      wide
      title={`Copy master data into ${tenant.name}`}
      description="Nothing is overwritten — a code this facility already has is left alone"
      footer={
        <>
          {missing.length > 0 && (
            <span className="me-auto self-center text-xs text-muted-foreground">
              Still needed: {missing.join(', ')}
            </span>
          )}
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              copy.mutate();
            }}
            loading={copy.isPending}
            disabled={missing.length > 0}
          >
            Copy
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Copy from"
          required
          hint={
            sources.length === 0
              ? 'No other facility of this kind on the platform yet'
              : 'Only facilities of the same kind are offered'
          }
        >
          <Select value={sourceId} onChange={(e) => setSourceId(e.target.value)} disabled={sources.length === 0}>
            <option value="">Choose a facility…</option>
            {sources.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name} ({facility.slug})
              </option>
            ))}
          </Select>
        </Field>

        <div>
          <p className="mb-2 text-sm font-medium">What to copy</p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {MASTER_COPY_GROUPS.map((group) => {
              const needs = MASTER_COPY_GROUP_DEPENDS_ON[group] ?? [];
              return (
                <label
                  key={group}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm ${
                    groups.includes(group) ? 'border-primary/40 bg-primary/5' : ''
                  }`}
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={groups.includes(group)}
                    onCheckedChange={(checked) => toggle(group, checked)}
                  />
                  <span>
                    {MASTER_COPY_GROUP_LABELS[group]}
                    {needs.length > 0 && (
                      <span className="block text-xs text-muted-foreground">
                        Needs {needs.map((n) => MASTER_COPY_GROUP_LABELS[n].toLowerCase()).join(', ')}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <Field label="Authenticator code" required hint="Writing a catalogue into a hospital needs a live code">
          <Input
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            className="w-32 tabular"
          />
        </Field>

        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
